import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/Login";
import Register from "./components/Register";
import ShortList from "./components/ShortList";
import DirectorProfile from "./components/DirectorProfile";
// import DiscountPopup from "./components/DiscountPopup";
import { 
  Building2, 
  Users, 
  CreditCard, 
  Bot, 
  ShoppingCart, 
  BarChart3, 
  Mail, 
  Phone, 
  MapPin, 
  Check, 
  Star,
  Menu,
  X,
  ChevronRight,
  Shield,
  Clock,
  Zap,
  Target,
  Award,
  Globe,
  Play,
  Pause,
  ChevronLeft,
  LogOut,
  UserCircle
} from "lucide-react";

// Use environment variable if available, otherwise use production backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || `${window.location.origin}/s`;
const API = `${BACKEND_URL}/api`;

// Check if director redirect is enabled via environment variable
const REDIRECT_TO_DIRECTOR = process.env.REACT_APP_REDIRECT_TO_DIRECTOR === 'true';

// Redirect Component - Redirects all routes to director profile if enabled
const DirectorRedirect = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    console.log('DirectorRedirect:', {
      enabled: REDIRECT_TO_DIRECTOR,
      currentPath: location.pathname,
      envVar: process.env.REACT_APP_REDIRECT_TO_DIRECTOR
    });
    
    if (REDIRECT_TO_DIRECTOR && location.pathname !== '/stephenpallam') {
      console.log('Redirecting to /stephenpallam');
      navigate('/stephenpallam', { replace: true });
    }
  }, [location, navigate]);
  
  return children;
};

// ScrollToTop Component - Auto scroll to top on route change
const ScrollToTop = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  return null;
};

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  
  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };
  
  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-lg text-gray-900">NexSite.ai</div>
              <div className="text-xs text-gray-400 font-medium -mt-1">AI Powered Business Solutions</div>
            </div>
          </Link>
          
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-gray-600 hover:text-orange-500 font-medium transition-colors text-sm">Home</Link>
            <Link to="/pricing" className="text-gray-600 hover:text-orange-500 font-medium transition-colors text-sm">Pricing</Link>
            {isAuthenticated && (
              <Link to="/short-list" className="text-gray-600 hover:text-orange-500 font-medium transition-colors text-sm">Short List</Link>
            )}
            <Link to="/contact" className="text-gray-600 hover:text-orange-500 font-medium transition-colors text-sm">Contact</Link>
            <Link to="/terms" className="text-gray-600 hover:text-orange-500 font-medium transition-colors text-sm">Terms</Link>
            <Link to="/privacy" className="text-gray-600 hover:text-orange-500 font-medium transition-colors text-sm">Privacy</Link>
            
            {isAuthenticated ? (
              <>
                <div className="flex items-center space-x-2 text-sm">
                  <UserCircle className="h-5 w-5 text-orange-500" />
                  <span className="text-gray-700 font-medium">
                    {user?.first_name} {user?.last_name}
                  </span>
                </div>
                <Button 
                  onClick={handleLogout}
                  variant="outline"
                  className="text-sm px-4 py-2 border-orange-500 text-orange-500 hover:bg-orange-50"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="outline" className="text-sm px-4 py-2">
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2">
                  <Link to="/register">Get Started</Link>
                </Button>
              </>
            )}
          </nav>
          
          <button 
            className="md:hidden text-gray-900"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-2 bg-white border-t border-gray-200">
            <Link to="/" className="block py-2 text-gray-600 hover:text-orange-500 text-sm">Home</Link>
            <Link to="/pricing" className="block py-2 text-gray-600 hover:text-orange-500 text-sm">Pricing</Link>
            {isAuthenticated && (
              <Link to="/short-list" className="block py-2 text-gray-600 hover:text-orange-500 text-sm">Short List</Link>
            )}
            <Link to="/contact" className="block py-2 text-gray-600 hover:text-orange-500 text-sm">Contact</Link>
            <Link to="/terms" className="block py-2 text-gray-600 hover:text-orange-500 text-sm">Terms</Link>
            <Link to="/privacy" className="block py-2 text-gray-600 hover:text-orange-500 text-sm">Privacy</Link>
            {isAuthenticated ? (
              <>
                <div className="py-2 text-gray-700 text-sm font-medium">
                  Hi, {user?.first_name}!
                </div>
                <Button 
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full text-sm"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" className="block py-2 text-gray-600 hover:text-orange-500 text-sm font-medium">Login</Link>
                <Link to="/register" className="block py-2 text-orange-500 hover:text-orange-600 text-sm font-medium">Register</Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

// Footer Component  
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-10 border-t border-gray-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-bold text-lg">NexSite.ai</div>
                <div className="text-xs text-gray-400 -mt-1">AI Powered Business Solutions</div>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              Empowering businesses with comprehensive digital solutions through our NexSite platform.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-3 text-orange-500 text-sm">Product</h3>
            <ul className="space-y-1 text-gray-400 text-sm">
              <li><Link to="/" className="hover:text-orange-500 transition-colors">NexSite CMS</Link></li>
              <li><Link to="/pricing" className="hover:text-orange-500 transition-colors">Pricing</Link></li>
              <li><a href="#features" className="hover:text-orange-500 transition-colors">Features</a></li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-3 text-orange-500 text-sm">Company</h3>
            <ul className="space-y-1 text-gray-400 text-sm">
              <li><Link to="/contact" className="hover:text-orange-500 transition-colors">Contact</Link></li>
              <li><Link to="/privacy" className="hover:text-orange-500 transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-orange-500 transition-colors">Terms of Use</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-3 text-orange-500 text-sm">Contact Info</h3>
            <div className="space-y-1 text-gray-400 text-sm">
              <div className="flex items-center space-x-2">
                <Mail className="h-3 w-3" />
                <span>info@nexsite.ai</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="h-3 w-3" />
                <span>(202) 290-7262</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-6 pt-6 text-center text-gray-400 text-xs">
          <p>&copy; 2025 Prime Pixel LLC dba NexSite.ai. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Video Demo Component
const VideoDemo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  return (
    <section className="py-8 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          {/* Left Column - Text Content (40% width) */}
          <div className="lg:col-span-2 space-y-4">
            <Badge className="bg-orange-100 text-orange-700 border border-orange-200 text-xs px-3 py-1 w-fit">
              <Bot className="h-3 w-3 mr-1" />
              AI Powered Business Platform
            </Badge>
            
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">
              Transform Your Business with{" "}
              <span className="text-orange-600">NexSite</span>
            </h1>
            
            <p className="text-base text-gray-700 font-medium">
              Dynamic, scalable, and AI-powered — everything your business needs in one place.
            </p>
            
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-start space-x-2">
                <span className="text-orange-500 font-bold">⚡</span>
                <span><strong>Dynamic Websites</strong></span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-orange-500 font-bold">⚡</span>
                <span><strong>HR & Payroll</strong> (Clock-in/out, Timesheets, Paystubs, Employee Scheduling)</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-orange-500 font-bold">⚡</span>
                <span><strong>POS & Payments</strong> (Clover, Toast, Square, Stripe, Link)</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-orange-500 font-bold">⚡</span>
                <span><strong>AI Marketing</strong> – Facebook, Instagram, WhatsApp, TikTok, X, Email & SMS (Pay-as-you-use AI Agents)</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-orange-500 font-bold">⚡</span>
                <span><strong>E-Commerce & Appointment Booking</strong></span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-orange-500 font-bold">⚡</span>
                <span><strong>Scalable Cloud Hosting</strong></span>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button asChild size="md" className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 text-sm">
                <Link to="/contact">Start Free Trial</Link>
              </Button>
              <Button asChild size="md" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 px-5 py-2 text-sm">
                <Link to="/pricing">View Pricing Plans</Link>
              </Button>
            </div>
          </div>

          {/* Right Column - Video Player (60% width) */}
          <div className="lg:col-span-3">
            <div className="relative bg-gray-100 rounded-lg overflow-hidden shadow-lg aspect-video">
              {/* AI/Bot Image - Replace with actual video */}
              <img 
                src="https://images.unsplash.com/photo-1758626099012-2904337e9c60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxBSSUyMGJ1c2luZXNzJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NTg4NDczMjZ8MA&ixlib=rb-4.1.0&q=85"
                alt="NexSite AI Platform Demo"
                className="w-full h-full object-cover"
              />
              
              {/* Video Overlay */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <button 
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full p-3 transition-all duration-200 hover:scale-110"
                  onClick={() => setIsPlaying(!isPlaying)}
                >
                  {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-1" />}
                </button>
              </div>
              
              {/* Video Duration/Progress */}
              <div className="absolute bottom-3 left-3 bg-black/70 text-white px-2 py-1 rounded text-xs">
                2:34 / 5:12
              </div>
            </div>
            
            {/* Video Description */}
            <div className="text-center mt-3 space-y-2">
              <h3 className="text-base font-semibold text-gray-900">NexSite AI Platform Demo</h3>
              <p className="text-xs text-gray-600">
                Watch how NexSite's AI agents automate your business operations—from smart marketing 
                to seamless POS integration and intelligent HR management.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Home Page Component
const Home = () => {
  return (
    <div className="min-h-screen pt-16 bg-white">
      {/* Discount Popup */}
      {/* <DiscountPopup /> */}
      
      {/* Video Demo Section */}
      <VideoDemo />

      {/* Features Section */}
      <section id="features" className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Business Management Suite</h2>
            <p className="text-sm text-gray-600 max-w-xl mx-auto">
              Everything your business needs in one powerful platform
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Globe className="h-6 w-6" />,
                title: "Dynamic Websites",
                description: "Custom websites for restaurants, hospitals, spas, and more with e-commerce and appointment booking systems"
              },
              {
                icon: <Users className="h-6 w-6" />,
                title: "HR Management",
                description: "Complete employee management with clock in/out, timesheets, and performance tracking"
              },
              {
                icon: <CreditCard className="h-6 w-6" />,
                title: "Payroll System",
                description: "Automated payroll processing for W2 and 1099 employees with pay stub generation"
              },
              {
                icon: <ShoppingCart className="h-6 w-6" />,
                title: "POS Integration",
                description: "Seamless integration with Clover, Toast, and Square for unified order management"
              },
              {
                icon: <Bot className="h-6 w-6" />,
                title: "AI Marketing",
                description: "ChatGPT-powered social media content generation and automated posting"
              },
              {
                icon: <BarChart3 className="h-6 w-6" />,
                title: "Analytics Dashboard",
                description: "Real-time insights and reporting across all business operations"
              }
            ].map((feature, index) => (
              <Card key={index} className="bg-white border border-gray-200 hover:shadow-md transition-all duration-200 hover-card">
                <CardContent className="p-5">
                  <div className="inline-flex p-2 rounded-lg mb-3 bg-orange-100 text-orange-600">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-gray-900">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Pricing Page Component
const Pricing = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    features: []
  });

  const plans = [
    {
      name: "Starter",
      price: 49,
      description: "Perfect for small businesses",
      features: [
        "Dynamic Website",
        "Basic HR Management", 
        "Payroll for up to 10 employees",
        "Basic Analytics",
        "Email Support"
      ],
      popular: false
    },
    {
      name: "Professional", 
      price: 79,
      description: "Ideal for growing businesses",
      features: [
        "Everything in Starter",
        "AI Marketing Tools",
        "POS Integration (1 platform)",
        "Advanced HR Features",
        "Payroll for up to 50 employees",
        "Priority Support"
      ],
      popular: true
    },
    {
      name: "Enterprise",
      price: 99,
      description: "Complete solution for large businesses", 
      features: [
        "Everything in Professional",
        "Multi-location Support",
        "All POS Integrations",
        "Custom Integrations",
        "Unlimited Employees",
        "24/7 Dedicated Support"
      ],
      popular: false
    }
  ];

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setFormData(prev => ({ ...prev, features: plan.features }));
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/subscription`, {
        ...formData,
        plan: selectedPlan.name
      });
      toast.success("Subscription inquiry sent! We'll contact you within 24 hours.");
      setIsModalOpen(false);
      setFormData({ name: '', email: '', phone: '', company: '', features: [] });
    } catch (error) {
      toast.error("Failed to send inquiry. Please try again.");
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-white">
      <section className="py-8 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Choose Your <span className="text-orange-600">NexSite</span> Plan
            </h1>
            <p className="text-sm text-gray-600 max-w-xl mx-auto">
              Flexible pricing plans designed to grow with your business. All plans include our core features with varying levels of support and customization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <Card key={index} className={`relative ${plan.popular ? 'border-orange-500 border-2 shadow-lg' : 'border-gray-200'} bg-white hover:shadow-md transition-all duration-200 hover-card`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-orange-500 text-white px-3 py-1 text-xs">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl font-bold text-gray-900">{plan.name}</CardTitle>
                  <div className="text-3xl font-bold text-orange-500 my-3">
                    ${plan.price}<span className="text-base text-gray-500">/month</span>
                  </div>
                  <CardDescription className="text-sm text-gray-600">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2 text-sm">
                        <Check className="h-4 w-4 text-orange-500" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => handlePlanSelect(plan)}
                    className={`w-full ${plan.popular ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-800 hover:bg-gray-900'} text-white`}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 mb-3 text-sm">Need a custom solution?</p>
            <Button asChild variant="outline" size="lg" className="border-orange-500 text-orange-500 hover:bg-orange-50">
              <Link to="/contact">Contact Sales</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Subscription Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md bg-white border border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Subscribe to {selectedPlan?.name} Plan</DialogTitle>
            <DialogDescription className="text-gray-600">
              Fill out this form and we'll contact you within 24 hours to set up your account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              className="border-gray-300"
            />
            <Input
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
              className="border-gray-300"
            />
            <Input
              type="tel"
              placeholder="Phone Number (Optional)"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="border-gray-300"
            />
            <Input
              placeholder="Company Name"
              value={formData.company}
              onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
              required
              className="border-gray-300"
            />
            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white">
              Submit Inquiry
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Contact Page Component  
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-white">
      <section className="py-8 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Get In Touch</h1>
            <p className="text-sm text-gray-600 max-w-xl mx-auto">
              Ready to transform your business? Let's discuss how NexSite can help you succeed.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <Card className="bg-white border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-gray-900">Send Us a Message</CardTitle>
                  <CardDescription className="text-gray-600">
                    Fill out the form and we'll get back to you within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        placeholder="Full Name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                        className="border-gray-300"
                      />
                      <Input
                        type="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        type="tel"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="border-gray-300"
                      />
                      <Input
                        placeholder="Company Name"
                        value={formData.company}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                        className="border-gray-300"
                      />
                    </div>
                    <Textarea
                      placeholder="Tell us about your business needs..."
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      required
                      className="border-gray-300"
                    />
                    <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              <Card className="bg-white border border-gray-200">
                <CardContent className="p-5">
                  <div className="flex items-center space-x-3">
                    <div className="bg-orange-100 p-2 rounded-lg">
                      <Mail className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email Us</h3>
                      <p className="text-sm text-gray-600">info@primepixel.net</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200">
                <CardContent className="p-5">
                  <div className="flex items-center space-x-3">
                    <div className="bg-orange-100 p-2 rounded-lg">
                      <Phone className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Call Us</h3>
                      <p className="text-sm text-gray-600">(202) 290-7262</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200">
                <CardContent className="p-5">
                  <div className="flex items-center space-x-3">
                    <div className="bg-orange-100 p-2 rounded-lg">
                      <MapPin className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Visit Us</h3>
                      <p className="text-sm text-gray-600">25644 Harvest Hills Dr<br />Chantilly, VA 20152</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold mb-3 text-gray-900">Business Hours</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Monday - Friday</span>
                      <span>9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday</span>
                      <span>10:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Team Page Component
const Team = () => {
  const teamMembers = [
    {
      name: "Alex Rodriguez",
      role: "CEO & Founder", 
      image: "https://images.pexels.com/photos/34027412/pexels-photo-34027412.jpeg",
      bio: "15+ years in enterprise software development. Previously led engineering teams at Microsoft and Google."
    },
    {
      name: "Sarah Chen",
      role: "CTO",
      image: "https://images.unsplash.com/photo-1579403124614-197f69d8187b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1ODgxOTgyMXww&ixlib=rb-4.1.0&q=85",
      bio: "Full-stack architect with expertise in AI/ML and cloud infrastructure. Stanford CS graduate."
    },
    {
      name: "Michael Johnson",
      role: "VP of Sales",
      image: "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3NTg4MTk4NTJ8MA&ixlib=rb-4.1.0&q=85",
      bio: "Proven track record in B2B SaaS sales. Helped scale three startups from seed to Series B."
    },
    {
      name: "Emily Davis",
      role: "Head of Product",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3NTg4MTk4NTJ8MA&ixlib=rb-4.1.0&q=85",
      bio: "Product strategist with deep understanding of SMB needs. Former product lead at Shopify and Square."
    },
    {
      name: "David Wilson", 
      role: "Head of Customer Success",
      image: "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxhbmFseXRpY3N8ZW58MHx8fHwxNzU4ODE5ODU4fDA&ixlib=rb-4.1.0&q=85",
      bio: "Customer success expert who ensures our clients achieve their business goals with NexSite."
    },
    {
      name: "Lisa Park",
      role: "VP of Marketing",
      image: "https://images.unsplash.com/photo-1707157281599-d155d1da5b4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxhbmFseXRpY3N8ZW58MHx8fHwxNzU4ODE5ODU4fDA&ixlib=rb-4.1.0&q=85",
      bio: "Growth marketing leader with expertise in B2B SaaS. MBA from Wharton, former marketing director at HubSpot."
    }
  ];

  return (
    <div className="min-h-screen pt-16 bg-white">
      <section className="py-8 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Meet Our Team</h1>
            <p className="text-sm text-gray-600 max-w-xl mx-auto">
              The passionate experts behind NexSite.ai, dedicated to transforming businesses through innovative technology solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamMembers.map((member, index) => (
              <Card key={index} className="hover:shadow-md transition-all duration-200 group bg-white border border-gray-200 hover-card">
                <CardContent className="p-5">
                  <div className="text-center">
                    <div className="relative w-24 h-24 mx-auto mb-3 overflow-hidden rounded-full">
                      <img 
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                      />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{member.name}</h3>
                    <p className="text-orange-600 font-medium mb-2 text-sm">{member.role}</p>
                    <p className="text-gray-600 text-xs leading-relaxed">{member.bio}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Join Our Growing Team</h2>
            <p className="text-gray-600 mb-4 text-sm">
              We're always looking for talented individuals who share our passion for innovation.
            </p>
            <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
              <Link to="/contact">View Open Positions</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

// Privacy Policy Page
const Privacy = () => {
  return (
    <div className="min-h-screen pt-16 bg-white">
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          </div>
          
          <div className="prose prose-base max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Effective Date:</strong> January 1, 2025
            </p>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                This Privacy Policy applies to services provided by <strong>Prime Pixel LLC</strong>, doing business as <strong>NexSite.ai</strong> ("we," "us," or "our"). 
                NexSite.ai is our comprehensive business management platform.
              </p>
            </div>
            
            <div className="space-y-6">
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">1. Information We Collect</h2>
                <p className="text-gray-700 mb-3 text-sm">
                  We collect information you provide directly to us, such as when you create an account, 
                  use our services, or contact us for support.
                </p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                  <li>Account information (name, email, company details)</li>
                  <li>Phone numbers for Two-Factor Authentication (2FA) if enabled</li>
                  <li>Business data processed through our NexSite platform</li>
                  <li>Support requests and communication records</li>
                  <li>Usage data and analytics</li>
                  <li>Device information when using our services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">2. Two-Factor Authentication (2FA) via SMS</h2>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-orange-800 mb-2">SMS Two-Factor Authentication</h3>
                  <div className="text-sm text-gray-700 space-y-2">
                    <p><strong>Purpose:</strong> If you enable Two-Factor Authentication, we will use your phone number solely to send one-time verification codes when you log in to your account.</p>
                    <p><strong>Optional Feature:</strong> 2FA is an optional security feature. Enabling it is highly recommended for enhanced account protection.</p>
                    <p><strong>How We Use Your Number:</strong> Your phone number is used exclusively for:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Sending verification codes during login when 2FA is enabled</li>
                      <li>Account security and recovery purposes</li>
                    </ul>
                    <p><strong>Disable 2FA:</strong> You can disable Two-Factor Authentication at any time through your account settings or by contacting support.</p>
                    <p><strong>SMS Rates:</strong> Standard message and data rates may apply when receiving verification codes.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">3. Email Communications</h2>
                <div className="text-sm text-gray-700 space-y-2">
                  <p><strong>Service Emails:</strong> By providing your email address, you consent to receive essential service-related emails including account notifications, security alerts, and support communications.</p>
                  <p><strong>Transactional Emails:</strong> We send transactional emails for account verification, password resets, and important service updates.</p>
                  <p><strong>Contact:</strong> You can contact us at any time for questions regarding your email preferences.</p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">4. How We Use Your Information</h2>
                <p className="text-gray-700 mb-3 text-sm">
                  We use the information we collect to provide, maintain, and improve our services:
                </p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                  <li>Deliver and operate the NexSite platform</li>
                  <li>Process transactions and send related information</li>
                  <li>Send service notifications and security alerts</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Send Two-Factor Authentication verification codes (if 2FA is enabled)</li>
                  <li>Comply with legal and regulatory requirements</li>
                  <li>Improve our products and develop new features</li>
                  <li>Prevent fraud and ensure platform security</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">5. Data Protection and Compliance</h2>
                <div className="text-sm text-gray-700 space-y-2">
                  <p><strong>Data Protection:</strong> We comply with applicable data protection regulations to safeguard your personal information.</p>
                  <p><strong>Data Retention:</strong> Personal information is retained only as long as necessary for providing our services and as required by law.</p>
                  <p><strong>2FA Data:</strong> Phone numbers used for Two-Factor Authentication are stored securely and used exclusively for verification purposes.</p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">6. Information Sharing</h2>
                <p className="text-gray-700 mb-3 text-sm">
                  We do not sell, trade, or otherwise transfer your personal information to third parties except:
                </p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                  <li>With your explicit consent</li>
                  <li>To comply with legal obligations and regulatory requirements</li>
                  <li>To protect our rights and safety</li>
                  <li>With trusted service providers under strict confidentiality agreements</li>
                  <li>With SMS service providers to deliver 2FA verification codes (if enabled)</li>
                  <li>To law enforcement when legally required</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">7. Data Security</h2>
                <p className="text-gray-700 text-sm">
                  We implement appropriate technical and organizational security measures to protect your 
                  personal information against unauthorized access, alteration, disclosure, or destruction. 
                  This includes encryption, secure servers, access logging, and regular security assessments.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">8. Your Rights</h2>
                <p className="text-gray-700 mb-3 text-sm">
                  You have the right to:
                </p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                  <li>Access and update your personal information</li>
                  <li>Request deletion of your data (subject to legal retention requirements)</li>
                  <li>Enable or disable Two-Factor Authentication at any time</li>
                  <li>Export your data</li>
                  <li>Update your phone number used for 2FA</li>
                  <li>File complaints with relevant data protection authorities</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">9. Contact Us</h2>
                <p className="text-gray-700 text-sm">
                  If you have questions about this Privacy Policy or need assistance with Two-Factor Authentication, please contact Prime Pixel LLC dba NexSite.ai at:
                </p>
                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-700 text-sm">
                    <strong>Company:</strong> Prime Pixel LLC dba NexSite.ai<br/>
                    <strong>Email:</strong> privacy@nexsite.ai<br/>
                    <strong>Phone:</strong> (202) 290-7262<br/>
                    <strong>Address:</strong> 25644 Harvest Hills Dr, Chantilly, VA 20152<br/>
                    <strong>2FA Support:</strong> Contact support to enable, disable, or update your 2FA settings
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Terms of Use Page
const Terms = () => {
  return (
    <div className="min-h-screen pt-16 bg-white">
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Terms of Use</h1>
          </div>
          
          <div className="prose prose-base max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Last Updated:</strong> January 1, 2025
            </p>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                These Terms of Use apply to services provided by <strong>Prime Pixel LLC</strong>, doing business as <strong>NexSite.ai</strong> ("we," "us," or "our"). 
                NexSite.ai is our comprehensive business management platform.
              </p>
            </div>
            
            <div className="space-y-6">
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">1. Acceptance of Terms</h2>
                <p className="text-gray-700 text-sm">
                  By accessing or using NexSite.ai platform operated by Prime Pixel LLC, you agree to be bound by these Terms of Use. 
                  If you do not agree to these terms, please do not use our services.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">2. Description of Service</h2>
                <p className="text-gray-700 mb-3 text-sm">
                  NexSite is a comprehensive business management platform that provides:
                </p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                  <li>Dynamic website creation and management</li>
                  <li>Human resources and payroll systems</li>
                  <li>Point of sale integrations</li>
                  <li>Business analytics and reporting</li>
                  <li>Two-Factor Authentication (2FA) via SMS for enhanced account security</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">3. Two-Factor Authentication (2FA)</h2>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-orange-800 mb-2">SMS Two-Factor Authentication</h3>
                  <div className="text-sm text-gray-700 space-y-2">
                    <p><strong>What is 2FA:</strong> Two-Factor Authentication adds an extra layer of security to your account by requiring a verification code sent to your phone number when logging in.</p>
                    <p><strong>How it works:</strong> When you enable 2FA, we will send a one-time verification code via SMS to your registered phone number each time you log in. You must enter this code to complete the login process.</p>
                    <p><strong>Optional Feature:</strong> Enabling 2FA is optional but highly recommended for enhanced account security.</p>
                    <p><strong>SMS Usage:</strong> By enabling 2FA, you consent to receive SMS verification codes for security purposes only. Standard message and data rates may apply.</p>
                    <p><strong>Disable 2FA:</strong> You may disable 2FA at any time through your account settings or by contacting support.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">4. User Responsibilities</h2>
                <p className="text-gray-700 mb-3 text-sm">You agree to:</p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                  <li>Provide accurate and current information including valid phone numbers and email addresses</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Use the service in compliance with applicable laws</li>
                  <li>Not use our services for spam, harassment, or illegal activities</li>
                  <li>Not interfere with or disrupt our services</li>
                  <li>Respect the intellectual property rights of others</li>
                  <li>Keep your 2FA-enabled phone number up to date if you have enabled Two-Factor Authentication</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">5. Security and Compliance</h2>
                <div className="text-sm text-gray-700 space-y-2">
                  <p><strong>Data Security:</strong> We implement industry-standard security measures to protect your account and personal information.</p>
                  <p><strong>2FA Security:</strong> When Two-Factor Authentication is enabled, verification codes are sent only to your registered phone number and are valid for a limited time.</p>
                  <p><strong>Documentation:</strong> We may require identity verification for account security purposes as mandated by applicable regulations.</p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">6. Payment Terms</h2>
                <p className="text-gray-700 text-sm">
                  Subscription fees are billed monthly or annually based on your selected plan. 
                  All fees are non-refundable except as required by law. We reserve the right to 
                  change our pricing with 30 days' notice.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">7. Prohibited Uses</h2>
                <div className="text-sm text-gray-700 space-y-2">
                  <p><strong>You may not use our services for:</strong></p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Harassment, threats, or abusive activities</li>
                    <li>Illegal activities or fraud</li>
                    <li>Impersonating others or providing false information</li>
                    <li>Distributing malware or harmful content</li>
                    <li>Attempting to bypass security measures including 2FA</li>
                  </ul>
                  <p><strong>Violations may result in immediate account termination and legal action.</strong></p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">8. Intellectual Property</h2>
                <p className="text-gray-700 text-sm">
                  The NexSite platform and all related intellectual property rights are owned by Prime Pixel LLC. 
                  You retain ownership of your data and content, but grant us a license to use it to provide our services.
                  We may monitor communications for compliance and quality assurance purposes.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">9. Limitation of Liability</h2>
                <p className="text-gray-700 text-sm">
                  Prime Pixel LLC shall not be liable for any indirect, incidental, special, consequential, 
                  or punitive damages resulting from your use of our services. Our total liability shall not 
                  exceed the amount paid by you in the 12 months prior to the claim. This includes damages 
                  related to communications delivery, compliance violations, or service interruptions.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">10. Termination</h2>
                <p className="text-gray-700 text-sm">
                  Either party may terminate this agreement at any time with 30 days' notice. 
                  We may immediately terminate accounts for violations of these terms or applicable laws.
                  Upon termination, your access to the service will cease, and we will provide 
                  you with an export of your data for 90 days (subject to legal retention requirements).
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">11. Contact Information</h2>
                <p className="text-gray-700 text-sm">
                  For questions regarding these Terms of Use or to get support with Two-Factor Authentication, contact Prime Pixel LLC dba NexSite.ai at:
                </p>
                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-700 text-sm">
                    <strong>Company:</strong> Prime Pixel LLC dba NexSite.ai<br/>
                    <strong>Email:</strong> legal@nexsite.ai<br/>
                    <strong>Phone:</strong> (202) 290-7262<br/>
                    <strong>Address:</strong> 25644 Harvest Hills Dr, Chantilly, VA 20152<br/>
                    <strong>2FA Support:</strong> Contact support to disable Two-Factor Authentication<br/>
                    <strong>General Inquiries:</strong> support@nexsite.ai
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App Component
function App() {
  useEffect(() => {
    // Test API connection - removed, not needed for production
    // Authentication and API calls will be made through AuthContext
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DirectorRedirect>
            <ScrollToTop />
            <Header />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/team" element={<Team />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/short-list" element={<ShortList />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/stephenpallam" element={<DirectorProfile />} />
            <Route path="/stepenpallam" element={<DirectorProfile />} />
            </Routes>
          </DirectorRedirect>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;